import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
} from '@angular/core';
import {
  FileService,
  notify,
  SiaArchivePath,
  SiaI18nService,
  SiaSessionService,
} from '@sia/shared';
import {
  create,
  createOptions,
  DocumentFormat,
  Interval,
  RichEdit,
  RichEditUnit,
  ViewType,
} from 'devexpress-richedit';

@Component({
  selector: 'richedit',
  template: '<div></div>',
  styleUrl: './sia-richedit-page.component.scss',
})
export class RicheditComponent implements AfterViewInit, OnDestroy {
  private element = inject(ElementRef);
  private fileSystemService = inject(FileService);
  private siaSessionService = inject(SiaSessionService);
  private i18nService = inject(SiaI18nService);

  file: SiaArchivePath = <SiaArchivePath>{};

  private rich: RichEdit | null = null;

  /** Inserted by Angular inject() migration for backwards compatibility */

  constructor() {}

  async ngAfterViewInit(): Promise<void> {
    this.file = this.siaSessionService.getSessionParamValue('file');
    // the createOptions() method creates an object that contains RichEdit options initialized with default values
    let base64: string = await this.fileSystemService.getBase64File(this.file);
    const options = createOptions();

    options.bookmarks!.visibility = true;
    options.bookmarks!.color = '#ff0000';

    options.confirmOnLosingChanges!.enabled = true;
    options.confirmOnLosingChanges!.message =
      'Are you sure you want to perform the action? All unsaved document data will be lost.';

    options.fields!.updateFieldsBeforePrint = true;
    options.fields!.updateFieldsOnPaste = true;

    options.mailMerge!.activeRecord = 2;
    options.mailMerge!.viewMergedData = true;
    options.mailMerge!.dataSource = [
      { Name: 'Indy', age: 32 },
      { Name: 'Andy', age: 28 },
    ];

    // events
    options.events.activeSubDocumentChanged = () => {};
    options.events.autoCorrect = () => {};
    options.events.calculateDocumentVariable = () => {};
    options.events.characterPropertiesChanged = () => {};
    options.events.contentInserted = () => {};
    options.events.contentRemoved = () => {};
    options.events.documentChanged = () => {};
    options.events.documentFormatted = () => {};
    options.events.documentLoaded = () => {};
    options.events.gotFocus = () => {};
    options.events.hyperlinkClick = () => {};
    options.events.keyDown = () => {};
    options.events.keyUp = () => {};
    options.events.paragraphPropertiesChanged = () => {};
    options.events.lostFocus = () => {};
    options.events.pointerDown = () => {};
    options.events.pointerUp = () => {};
    options.events.saving = async (s, e) => {
      this.file.base64 = e.base64;
      var res = await this.fileSystemService.saveBase64File(this.file);
      if (res) {
        notify('Salvato con successo', 2000, 'success');
      }
    };
    options.events.saved = () => {};
    options.events.selectionChanged = () => {};
    options.events.customCommandExecuted = (s, e) => {
      switch (e.commandName) {
        case 'insertEmailSignature':
          s.document.insertParagraph(s.document.length);
          s.document.insertText(s.document.length, '_________');
          s.document.insertParagraph(s.document.length);
          s.document.insertText(s.document.length, 'Best regards,');
          s.document.insertParagraph(s.document.length);
          s.document.insertText(s.document.length, 'John Smith');
          s.document.insertParagraph(s.document.length);
          s.document.insertText(s.document.length, 'john@example.com');
          s.document.insertParagraph(s.document.length);
          s.document.insertText(s.document.length, '+1 (818) 844-0000');
          break;
      }
    };

    options.unit = RichEditUnit.Inch;

    options.view!.viewType = ViewType.PrintLayout;
    options.view!.simpleViewSettings!.paddings = {
      left: 15,
      top: 15,
      right: 15,
      bottom: 15,
    };

    options.autoCorrect = {
      correctTwoInitialCapitals: true,
      detectUrls: true,
      enableAutomaticNumbering: true,
      replaceTextAsYouType: true,
      caseSensitiveReplacement: false,
      replaceInfoCollection: [
        { replace: 'wnwd', with: 'well-nourished, well-developed' },
        { replace: '(c)', with: '©' },
      ],
    };
    // capitalize the first letter at the beginning of a new sentence/line
    options.events.autoCorrect = function (s, e) {
      if (e.text.length == 1 && /\w/.test(e.text)) {
        var prevText = s.document.getText(
          new Interval(e.interval.start - 2, 2),
        );
        if (
          prevText.length == 0 ||
          /^(\. |\? |\! )$/.test(prevText) ||
          prevText.charCodeAt(1) == 13
        ) {
          var newText = e.text.toUpperCase();
          if (newText != e.text) {
            s.beginUpdate();
            s.history.beginTransaction();
            s.document.deleteText(e.interval);
            s.document.insertText(e.interval.start, newText);
            s.history.endTransaction();
            s.endUpdate();
            e.handled = true;
          }
        }
      }
    };

    options.exportUrl = 'https://siteurl.com/api/';

    options.readOnly = false;
    options.width = '100%';
    options.height = '100%';
    this.rich = create(this.element.nativeElement.firstElementChild, options);
    if (this.file.extension == '.txt') {
      this.rich.openDocument(base64, this.file.path, DocumentFormat.PlainText);
    } else {
      this.rich.openDocument(base64, this.file.path, DocumentFormat.OpenXml);
    }
  }

  ngOnDestroy() {
    if (this.rich) {
      this.rich.dispose();
      this.rich = null;
    }
  }
}
